import ApiService from '../../services/api.service'
import { Notify } from 'quasar'

/**
 * Helper pour récupérer les occurrences à partir de différents points de terminaison.
 * @param {Function} commit - Vuex commit function.
 * @param {string} endpoint - The API endpoint to fetch from.
 * @param {object} params - The parameters for the request.
 * @returns {Promise<Array>} The list of occurrences.
 */
async function genericFetchOccurrences({ commit }, endpoint, params) {
  try {
    const response = await ApiService.getRequest(endpoint, params);
    // Les endpoints d'occurrences retournent directement un tableau dans response.data
    const occurrences = response.data;
    commit('SET_OCCURRENCES', occurrences);
    return occurrences;
  } catch (error) {
    console.error(`Erreur lors de la récupération des occurrences depuis ${endpoint}:`, error);
    Notify.create({
      message: "Une erreur s'est produite lors de la récupération des occurrences.",
      color: 'red'
    });
    throw error;
  }
}

export const schedulerEvents = {
  namespaced: true,
  state: {
    schedulerEvents: [],
    schedulerEvent: null,
    occurrences: []
  },
  mutations: {
    setSchedulerEvents(state, schedulerEvents) {
      state.schedulerEvents = schedulerEvents
    },
    setSchedulerEvent(state, schedulerEvent) {
      state.schedulerEvent = schedulerEvent
    },
    ADD_EVENT(state, newEvent) {
      state.schedulerEvents.push(newEvent)
    },
    UPDATE_EVENT(state, updatedEvent) {
      const index = state.schedulerEvents.findIndex(
        (event) => event.id === updatedEvent.id
      )
      if (index !== -1) {
        state.schedulerEvents.splice(index, 1, updatedEvent)
      }
    },
    REMOVE_EVENT(state, eventId) {
      state.schedulerEvents = state.schedulerEvents.filter(
        (event) => event.id !== eventId
      )
    },
    SET_OCCURRENCES(state, occurrences) {
      state.occurrences = occurrences
    }
  },
  actions: {
    // --- Actions de production ---
    async fetchSchedulerEventByMonth(context, data) {
      return await ApiService.getRequest(`/scheduler_events_month/${data.dateStart}/${data.dateEnd}/${data.antenne}`)
        .then((response) => {
          context.commit('setSchedulerEvents', response.data)
          return response.data
        })
        .catch((error) => {
          console.error(error)
          Notify.create({
            message: "Une erreur s'est produite",
            color: 'red'
          })
        })
    },
    fetchSchedulerEvent(context, idEvent) {
      try {
        return ApiService.getRequest(`/scheduler_events/${idEvent}`).then(
          (response) => {
            context.commit('setSchedulerEvent', response.data)
            return response.data
          }
        )
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },
    fetchSchedulerEventsSpeaker(context, data) {
      return ApiService.getRequest('/scheduler_occurences_speaker/', {
        start: data.start,
        end: data.end,
        speaker_id: data.speaker_id
      })
        .then((response) => {
          context.commit('setSchedulerEvents', response.data)
          return response.data
        })
        .catch((error) => {
          console.error(error)
          Notify.create({
            message: "Une erreur s'est produite",
            color: 'red'
          })
        })
    },
    // POST :: Ajoute un nouvel evenement
    async addSchedulerEvents(context, newEvent) {
      try {
        const response = await ApiService.postRequest(
          'scheduler_events',
          newEvent
        )
        context.commit('ADD_EVENT', response.data)
        Notify.create({
          message: "L'évènement a été créé avec succès.",
          color: 'green'
        })
        return response.data
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors de la création de l'évènement.",
          color: 'red'
        })
        throw error
      }
    },
    // --- CRUD Events (refactorisation) ---
    async createSchedulerEvent({ commit }, eventData) {
      try {
        const response = await ApiService.postRequest(
          'scheduler_events',
          eventData
        )
        commit('ADD_EVENT', response.data)
        Notify.create({
          message: "L'évènement a été créé avec succès.",
          color: 'green'
        })
        return response.data
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors de la création de l'évènement.",
          color: 'red'
        })
        throw error
      }
    },
    async updateSchedulerEvent({ commit }, { id, data }) {
      try {
        //Le backend attend une requête POST sur l'item route pour les mises à jour [(Au lieu d'un PATCH ou PUT)].
        const url = id.startsWith('/') ? id.substring(1) : id
        const response = await ApiService.postRequest(url, data)
        commit('UPDATE_EVENT', response.data)
        Notify.create({
          message: "L'évènement a été mis à jour avec succès.",
          color: 'green'
        })
        return response.data
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors de la mise à jour de l'évènement.",
          color: 'red'
        })
        throw error
      }
    },
    async deleteSchedulerEvent({ commit }, eventId) {
      try {
        await ApiService.deleteRequest(`scheduler_events/${eventId}`)
        commit('REMOVE_EVENT', eventId)
        Notify.create({
          message: "L'évènement a été supprimé avec succès.",
          color: 'green'
        })
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors de la suppression de l'évènement.",
          color: 'red'
        })
        throw error
      }
    },
    async fetchEventById({ commit }, eventId) {
      try {
        const response = await ApiService.getRequest(
          `scheduler_events/${eventId}`
        )
        commit('setSchedulerEvent', response.data)
        return response.data
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors de la récupération de l'évènement.",
          color: 'red'
        })
        throw error
      }
    },

    async addRecurrenceExdate({ commit }, { id, data }) {
      try {
        //Cette action utilise spécifiquement PATCH pour l'endpoint exdate.
        const url = `scheduler_events/${id}`
        const response = await ApiService.patchRequest(url, data)
        // TODO: Il pourrait être nécessaire de mettre à jour l'événement dans le store ici.
        return response.data
      } catch (error) {
        console.error("Erreur lors de l'ajout de la date d'exclusion:", error)
        Notify.create({
          message:
            "Une erreur s'est produite lors de la suppression de l'occurrence.",
          color: 'red'
        })
        throw error
      }
    },

    // --- Occurrences ---
    fetchSchedulerOccurrences(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences/',
        params
      )
    },
    fetchOccurrencesByClassroom(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_classroom/',
        params
      )
    },
    fetchOccurrencesByParticipant(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_participant/',
        params
      )
    },
    fetchOccurrencesBySpeaker(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_speaker/',
        params
      )
    },
    fetchOccurrencesByEquipment(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_equipment/',
        params
      )
    },
    fetchOccurrencesByGroupe(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_groupe/',
        params
      )
    },
    fetchOccurrencesByActionSession(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_action_session/',
        params
      )
    },
    fetchOccurrencesByDiplomeSession(context, params) {
      return genericFetchOccurrences(
        context,
        '/scheduler_occurences_diplome_session/',
        params
      )
    },

    // --- Recurrence ---
    async detachOccurrence(context, { parentId, occurrenceDate, modifications = null }) {
      try {
        const payload = { occurrence_date: occurrenceDate }

        // Si des modifications sont fournies, les inclure dans le payload
        if (modifications) {
          payload.modifications = modifications
        }

        const response = await ApiService.postRequest(
          `scheduler_events/${parentId}/detach`,
          payload
        )
        // Optionally, you might want to refresh data or update the state here
        Notify.create({
          message: "L'occurrence a été détachée avec succès.",
          color: 'green'
        })
        return response.data
      } catch (error) {
        Notify.create({
          message:
            "Une erreur s'est produite lors du détachement de l'occurrence.",
          color: 'red'
        })
        throw error
      }
    },


  },
  getters: {
    getSchedulerEvents(state) {
      return state.schedulerEvents
    },
    getSchedulerEvent(state) {
      return state.schedulerEvent
    },
    getOccurrences(state) {
      return state.occurrences
    },
    getEventById: (state) => (id) => {
      return state.schedulerEvents.find((event) => event.id === id)
    }
  }
}
